import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { UploadImg } from 'src/components/UploadImg/UploadImg';
import { HomePageCarouselRedirectOptions } from './HomePageCarouselRedirectOptions';
import { HomePageCarouselFilter } from './HomePageCarouselFilter';
import DatePicker from 'react-datepicker';
import { Popup } from 'src/domains/layouts/shared/popup/Popup';
import { SuccessPopup } from 'src/domains/layouts/shared/popup/popups/cms/SuccessPopup';
import { InputError, InputLabel } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import {
    FormComponent,
    FormWrapper,
    DatesWrapper,
    DateInputWrapper,
} from 'src/domains/layouts/shared/cms/form/Form.style';
import { HomePageCarouselDateState } from './HomePageCarouselDateState';
import { HomePageCarouselState } from 'src/AppState/CmsState/HomePageCarouselState/HomePageCarouselState';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/Select';
import { ParagraphDefault, Label } from 'src/domains/layouts/shared/typography/Content.style';
import { PromoCarouselTranslationsForm } from './PromoCarouselTranslationsForm';
import { AddTranslationButton } from 'src/domains/layouts/shared/cms/translationsForm/CmsTranslationsForm.style';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

interface HomePageCarouselPromoFormPropsType {
    carouselPromoState: HomePageCarouselState;
    dataTest: string;
}

export const HomePageCarouselPromoForm = observer(
    ({ carouselPromoState, dataTest }: HomePageCarouselPromoFormPropsType) => {
        const [state] = useState(() => new HomePageCarouselDateState(carouselPromoState));
        const { onChangeStartDate, onBlurStartDate, onChangeEndDate, onBlurEndDate, startDateError, endDateError } =
            state;

        const {
            title,
            subtitle,
            btnTitle,
            btnColor,
            bgColor,
            tcText,
            pageName,
            actionTemplate,
            redirectOptions,
            selectedDevice,
            deviceOptions,
            uploadIcon,
            handleCreate,
            handleReset,
            resetForm,
            isEditMode,
            isLoading,
            startDate,
            endDate,
            isHomePage,
            isCasinoPage,
            onChangeDevice,
            onChangeActionTemplate,
            handleHideCreatedPopup,
            showCreatedPopup,
            isDefaultPromo,
            languagesState,
            idsList,
            stakeFactor,
            xtremepushCampaignUrl,
        } = carouselPromoState;

        const common = useCommon();
        const configState = ConfigState.get(common);

        return (
            <FormWrapper
                isEditMode={isEditMode}
                data-test={dataTest}
            >
                {isHomePage || isCasinoPage ? (
                    <>
                        <HomePageCarouselFilter carouselPromoState={carouselPromoState} />
                        <ParagraphDefault>
                            Default Promos are cards with a Call-to-Action (CTA) button that will take the user to a
                            different section of the site. To add another language, click the Add Language button below.
                        </ParagraphDefault>
                    </>
                ) : (
                    <FormHeader
                        isEditMode={isEditMode}
                        defaultLabel={`Create new ${pageName} carousel`}
                        editModeLabel={`Edit ${pageName} carousel`}
                        resetForm={resetForm}
                    >
                        Default Promos are cards with a Call-to-Action (CTA) button that will take the user to a
                        different section of the site. To add another language, click the Add Language button below.
                    </FormHeader>
                )}

                <FormComponent
                    onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                        e.preventDefault();
                    }}
                >
                    <Loader isActive={isLoading} />
                    <Textarea
                        formState={title}
                        isEmpty={title.value === ''}
                        label='Title'
                        dataTest='promo-title'
                    />
                    <Textarea
                        formState={subtitle}
                        isEmpty={subtitle.value === ''}
                        label='Subtitle'
                        dataTest='promo-subtitle'
                    />
                    <Input
                        formInputState={btnTitle}
                        isEmpty={btnTitle.value === ''}
                        label='Button call to action text'
                        dataTest='call-to-action-button-text'
                    />
                    <>
                        {carouselPromoState.formsIds.map((item) => {
                            const state = carouselPromoState.getTranslationFormStateById(item);
                            return (
                                <PromoCarouselTranslationsForm
                                    key={item}
                                    promoTranslationsState={state}
                                    promoCarouselState={carouselPromoState}
                                    languagesState={languagesState}
                                    isDefaultPromo={isDefaultPromo}
                                    formId={item}
                                />
                            );
                        })}
                        {carouselPromoState.formsIds.length === 0 &&
                        languagesState.getNonDefaultLanguagesList().length > 0 ? (
                            <AddTranslationButton
                                addAnotherForm={false}
                                version='secondary'
                                width='full'
                                onClick={carouselPromoState.addLanguageForm}
                            >
                                Add language
                            </AddTranslationButton>
                        ) : null}
                    </>
                    <Input
                        formInputState={btnColor}
                        isEmpty={btnColor.value === ''}
                        label='Button color (#hex value only)'
                        dataTest='button-colour'
                    />
                    <Select
                        stateValue={actionTemplate}
                        selectOptions={redirectOptions}
                        defaultText='Select redirection'
                        onChangeCB={onChangeActionTemplate}
                        dataTest='select-redirection-homepage'
                    />
                    <HomePageCarouselRedirectOptions carouselPromoState={carouselPromoState} />
                    <Input
                        formInputState={bgColor}
                        isEmpty={bgColor.value === ''}
                        label='Background color (#hex value only)'
                        dataTest='background-colour'
                    />
                    <UploadImg
                        title='Image'
                        uploadImageState={uploadIcon}
                        hideDeleteButton={configState.config.hideDeleteImageButtonOnHomePageCarousel}
                    />
                    <Textarea
                        formState={tcText}
                        isEmpty={tcText.value === ''}
                        label='Terms'
                        ckeditor
                    />
                    <DatesWrapper>
                        <DateInputWrapper isEmpty={startDate === null}>
                            <InputLabel isEmpty={startDate === null}>Start date *</InputLabel>
                            <DatePicker
                                id='Start-date'
                                selected={startDate}
                                onChange={onChangeStartDate}
                                onBlur={onBlurStartDate}
                                showTimeSelect
                                timeFormat='HH:mm'
                                timeIntervals={15}
                                timeCaption='time'
                                dateFormat='MMMM d, yyyy h:mm aa'
                            />
                            {startDateError ? <InputError>This field is required.</InputError> : null}
                        </DateInputWrapper>
                        <DateInputWrapper isEmpty={endDate === null}>
                            <InputLabel isEmpty={endDate === null}>End date *</InputLabel>
                            <DatePicker
                                id='End-date'
                                selected={endDate}
                                onChange={onChangeEndDate}
                                onBlur={onBlurEndDate}
                                showTimeSelect
                                timeFormat='HH:mm'
                                timeIntervals={15}
                                timeCaption='time'
                                dateFormat='MMMM d, yyyy h:mm aa'
                            />
                            {endDateError ? <InputError>This field is required.</InputError> : null}
                        </DateInputWrapper>
                    </DatesWrapper>
                    <Select
                        label='Select device'
                        stateValue={selectedDevice}
                        selectOptions={deviceOptions}
                        defaultText='Select device'
                        onChangeCB={onChangeDevice}
                    />
                    <Label>
                        Display this promotion only to the following users (optional): [user ids separated by commas].
                    </Label>
                    <Textarea
                        placeholder='Use comma or line break to separate user ids'
                        formState={idsList}
                    />
                    <Label>Display this promotion only to users with specified settings.</Label>
                    <Select
                        label='Exclude promo banned customers'
                        stateValue={carouselPromoState.promoBannedCustomerValue}
                        selectOptions={carouselPromoState.promoBannedCustomerOptions}
                        defaultText='Select option'
                        onChangeCB={carouselPromoState.onChangePromoBannedCustomerOptions}
                    />

                    <Select
                        label='Hidden when logged in'
                        stateValue={carouselPromoState.hiddenLoggedInValue}
                        selectOptions={carouselPromoState.hiddenLoggedInOptions}
                        defaultText='Select option'
                        onChangeCB={carouselPromoState.onChangeHiddenLoggedInOptions}
                    />

                    <Select
                        label='Toggle to show only if customer is email opted in or out'
                        stateValue={carouselPromoState.emailOptedValue}
                        selectOptions={carouselPromoState.emailOpteSwitchdOptions}
                        defaultText='Select option'
                        onChangeCB={carouselPromoState.onChangeEmailOptedOptions}
                    />

                    <Select
                        label='Stake factor type'
                        stateValue={carouselPromoState.stakeFactorValue}
                        selectOptions={carouselPromoState.stakeFactorSwitchOption}
                        defaultText='Select stake factor type'
                        onChangeCB={carouselPromoState.onChangeStakeFactorOptions}
                    />
                    {carouselPromoState.stakeFactorValue === 'list' ? (
                        <Input
                            formInputState={stakeFactor}
                            isEmpty={stakeFactor.value === ''}
                            label='For users with the specified stake factor as list e.g. 1.2, 2.3, 3.4'
                            dataTest='stake-factor'
                        />
                    ) : null}
                    {carouselPromoState.stakeFactorValue === 'range' ? (
                        <DatesWrapper>
                            <DateInputWrapper isEmpty={startDate === null}>
                                <Input
                                    formInputState={carouselPromoState.stakeFactorRangeMin}
                                    isEmpty={carouselPromoState.stakeFactorRangeMin.value === ''}
                                    label='Min stake factor (inclusive)'
                                    dataTest='stake-factor-range-min'
                                />
                            </DateInputWrapper>
                            <DateInputWrapper isEmpty={startDate === null}>
                                <Input
                                    formInputState={carouselPromoState.stakeFactorRangeMax}
                                    isEmpty={carouselPromoState.stakeFactorRangeMax.value === ''}
                                    label='Max stake factor (inclusive)'
                                    dataTest='stake-factor-range-max'
                                />
                            </DateInputWrapper>
                        </DatesWrapper>
                    ) : null}

                    {configState.config.hasXtremepush ? (
                        <Input
                            formInputState={xtremepushCampaignUrl}
                            isEmpty={xtremepushCampaignUrl.value === ''}
                            label='Xtremepush Campaign URL'
                            dataTest='xtremepush-campaign-url'
                        />
                    ) : null}

                    <FormButtons
                        onCreate={handleCreate}
                        onUpdate={carouselPromoState.handleEdit}
                        onCancel={handleReset}
                        isEditMode={isEditMode}
                        dataTestCreate='create-button'
                    />

                    {showCreatedPopup === false ? null : (
                        <Popup
                            width={384}
                            handleHidePopup={handleHideCreatedPopup}
                        >
                            <SuccessPopup
                                handleHidePopup={handleHideCreatedPopup}
                                itemLabel='promo carousel'
                            />
                        </Popup>
                    )}
                </FormComponent>
            </FormWrapper>
        );
    }
);
